import { Inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { sharedConstants } from 'src/environments/constants';
import { UserDataShared } from '../models/interface/userData.interface';
import { MuDeliveryService } from '../sharedProtos/delivery/mu-delivery.service';
import { MuGeologicService } from '../sharedProtos/geologic/mu-geologic.service';
import { UtilsService } from './utils.service';
import { CHAT_EXPERIMENT_CONFIG, ChatCountryCode, ChatExperimentConfig } from './chat-experiment.config';

const toCityId = (value: unknown): number | null => (Number.isInteger(value) && (value as number) > 0 ? (value as number) : null);

/** Snapshot taken at load time: environment.countries objects are shared and can be mutated elsewhere. */
const COUNTRIES = environment.countries.map(({ id, geoName, center }) => ({ id, geoName, center: { ...center } }));

/** Resolves the city sent to the chat experiment. Never triggers the browser location prompt. */
@Injectable({
  providedIn: 'root',
})
export class ChatCityResolver {
  constructor(
    @Inject(CHAT_EXPERIMENT_CONFIG) private config: ChatExperimentConfig,
    private geologicService: MuGeologicService,
    private deliveryService: MuDeliveryService,
    private utilsService: UtilsService
  ) {}

  forUser(userData: UserDataShared | null): number | null {
    return toCityId(userData?.cityId) ?? toCityId(userData?.companyCityId);
  }

  forCountry(country: ChatCountryCode): number {
    return this.config.capitalCityIds[country];
  }

  /** City of the current position, only when location access was granted before; null otherwise. */
  async fromGeolocation(): Promise<number | null> {
    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      if (permission.state !== 'granted') {
        return null;
      }
      const { latitude, longitude } = await this.currentPosition();
      const country = this.nearestCountry(latitude, longitude);
      const address = await this.geologicService.getAddressByCoordinates(country.geoName, latitude, longitude);
      const { cityList } = await this.deliveryService.getCityList(sharedConstants.statusToFilterCity, country.id);
      return toCityId(this.utilsService.findCityInList(address.cityGeoName, cityList)?.id);
    } catch {
      return null;
    }
  }

  private currentPosition(): Promise<GeolocationCoordinates> {
    return new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(position => resolve(position.coords), reject));
  }

  private nearestCountry(latitude: number, longitude: number): { id: number; geoName: string } {
    const distance = (center: { lat: number; lng: number }) => (center.lat - latitude) ** 2 + (center.lng - longitude) ** 2;
    return [...COUNTRIES].sort((a, b) => distance(a.center) - distance(b.center))[0];
  }
}
